"use client";

import type React from "react";
import { EspeceFormData } from "@repo/types";
import { AlertCircle, ImageIcon, Upload } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";

// Define interface for form with string tarif


interface EspeceFormFieldsProps {
  form: UseFormReturn<EspeceFormData>;
  error: string | null;
  isEditing: boolean;
  isSubmitting: boolean;
  previewUrl: string | null;
  onCancel: () => void;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: EspeceFormData) => Promise<void>;
}

export function EspeceFormFields({
  form,
  error,
  isEditing,
  isSubmitting,
  previewUrl,
  onCancel,
  handleImageChange,
  onSubmit,
}: EspeceFormFieldsProps) {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="space-y-2 pb-8">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
          {isEditing ? "Modifier une espèce" : "Créer une nouvelle espèce"}
        </h2>
        <p className="text-base text-muted-foreground">
          {isEditing
            ? "Modifiez les informations de l'espèce existante."
            : "Remplissez le formulaire pour créer une nouvelle espèce."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="genre"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-semibold text-gray-900">Genre</FormLabel>
                    <FormControl>
                      <Input placeholder="Genre" {...field} value={field.value || ""} className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-semibold text-gray-900">Espèce *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom de l'espèce" {...field} value={field.value || ""} className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nom_fr"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-semibold text-gray-900">Nom français</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom français de l'espèce" {...field} value={field.value || ""} className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="cultivar"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-base font-semibold text-gray-900">Cultivar</FormLabel>
                      <FormControl>
                        <Input placeholder="Cultivar" {...field} value={field.value || ""} className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="categorie"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-base font-semibold text-gray-900">Catégorie</FormLabel>
                      <FormControl>
                        <Input placeholder="Catégorie" {...field} value={field.value || ""} className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="tarif"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-base font-semibold text-gray-900">Tarif</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Tarif"
                        {...field}
                        value={field.value || ""}
                        className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                        onChange={(e) => {
                          const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Image de l'espèce
              </h3>
              <div className="space-y-4">
                <div className="relative">
                  <Input id="image" type="file" accept="image/*" onChange={handleImageChange} className="sr-only" />
                  <FormLabel
                    htmlFor="image"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                      </p>
                      <p className="text-xs text-gray-400">PNG, JPG, JPEG (MAX. 10MB)</p>
                    </div>
                  </FormLabel>
                </div>
                {previewUrl && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
                    <div className="aspect-video relative">
                      <img
                        src={previewUrl}
                        alt="Aperçu de l'image"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ImageIcon className="w-4 h-4" />
                        <span>Aperçu de l'image sélectionnée</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onCancel} className="h-11 px-6 border-gray-300 hover:bg-gray-50 bg-transparent">
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting} className="h-11 px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg disabled:opacity-50">
              {isSubmitting ? "Enregistrement..." : isEditing ? "Mettre à jour" : "Créer"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
